import { useEffect, useState } from 'react';
import type { CollectionHistory as History, CollectionJob } from '../shared/collections';
import { collectionJobRetryable } from '../shared/collections';
import type { State } from '../shared/model';

const stamp = (value: string) =>
  new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
const labels: Record<CollectionJob['status'], string> = {
  running: 'Em andamento',
  succeeded: 'Concluída',
  partial: 'Parcial',
  failed: 'Falhou',
  skipped: 'Sem coleta',
  missed: 'Horário perdido',
};
const itemLabels: Record<CollectionJob['items'][number]['status'], string> = {
  pending: 'Pendente',
  succeeded: 'Leitura salva',
  failed: 'Falhou',
  skipped: 'Sem leitura',
  not_applicable: 'Não se aplica',
};

export function CollectionHistory({
  campaignId,
  revision,
  data,
}: {
  campaignId: string;
  revision?: number;
  data: State;
}) {
  const [history, setHistory] = useState<History>();
  const [error, setError] = useState('');
  const [request, setRequest] = useState(0);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState('');
  async function retry(job: CollectionJob) {
    setRetrying(job.id);
    setError('');
    try {
      const response = await fetch(
        `/api/campaigns/${encodeURIComponent(campaignId)}/collection/jobs/${encodeURIComponent(job.id)}/retry`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
          body: JSON.stringify({ version: job.version }),
        },
      );
      const value = await response.json();
      if (!response.ok)
        throw new Error(value.error || 'Não foi possível programar uma nova tentativa.');
      setRequest((current) => current + 1);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setRetrying('');
    }
  }
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/collection/history`)
      .then(async (response) => {
        const value = await response.json();
        if (!response.ok) throw new Error(value.error || 'Não foi possível consultar o histórico.');
        if (current) setHistory(value);
      })
      .catch((reason) => {
        if (current) setError(reason.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [campaignId, revision, request]);
  return (
    <div className="cw-collection-history">
      <div className="cw-section-heading">
        <h3>Execuções e fechamento D+7</h3>
        <button type="button" onClick={() => setRequest((value) => value + 1)} disabled={loading}>
          Atualizar histórico
        </button>
      </div>
      {error && <p role="alert">{error}</p>}
      {history && (
        <>
          <p>
            {history.nextDueAt
              ? `Próxima execução: ${stamp(history.nextDueAt)}`
              : 'Nenhuma execução programada nas condições atuais.'}
          </p>
          <p className="fine-print">
            Além dos horários acima, cada publicação com instante confirmado recebe um fechamento
            sete dias depois. Se a coleta ocorrer com atraso, o horário efetivo permanece explícito.
          </p>
          {history.pending
            .filter((item) => item.status === 'pending')
            .map((item) => (
              <p key={item.id} className="notice">
                {item.message}{' '}
                {item.url && (
                  <a href={item.url} target="_blank" rel="noreferrer">
                    Conferir publicação
                  </a>
                )}
              </p>
            ))}
          {!history.jobs.length ? (
            <p>Nenhuma execução registrada. Ativar a coleta começa um histórico novo.</p>
          ) : (
            <div className="cw-collection-jobs">
              {history.jobs.map((job) => (
                <details key={job.id}>
                  <summary>
                    <strong>{job.kind === 'd7' ? 'Fechamento D+7' : `Coleta ${job.slot}`}</strong> ·{' '}
                    {job.dueAt ? stamp(job.dueAt) : job.date} · {labels[job.status]}
                  </summary>
                  <p>
                    {job.attempts} {job.attempts === 1 ? 'tentativa' : 'tentativas'}
                    {job.nextAttemptAt ? ` · Nova tentativa em ${stamp(job.nextAttemptAt)}` : ''}
                  </p>
                  {job.lastError && <p>{job.lastError}</p>}
                  {data.permissions.admin &&
                    history.plan.enabled &&
                    data.campaigns.find((campaign) => campaign.id === campaignId)?.status ===
                      'ativa' &&
                    ['failed', 'partial', 'skipped'].includes(job.status) &&
                    !job.nextAttemptAt &&
                    collectionJobRetryable(job) && (
                      <button type="button" disabled={!!retrying} onClick={() => void retry(job)}>
                        {retrying === job.id ? 'Programando…' : 'Tentar novamente'}
                      </button>
                    )}
                  {!!job.retryRequests?.length && (
                    <p>
                      {job.retryRequests.length}{' '}
                      {job.retryRequests.length === 1
                        ? 'nova rodada solicitada'
                        : 'novas rodadas solicitadas'}
                      . A data prevista original é preservada.
                    </p>
                  )}
                  {job.items.map((item) => (
                    <div key={item.key} className="cw-collection-item">
                      <strong>{item.publication?.title || item.key}</strong>
                      {item.publication && (
                        <a href={item.publication.url} target="_blank" rel="noreferrer">
                          {item.publication.account} · Ver publicação
                        </a>
                      )}
                      <span>{itemLabels[item.status]}</span>
                      <span>
                        {item.collectedAt ? stamp(item.collectedAt) : 'Sem horário de coleta'}
                      </span>
                      {item.error && <p>{item.error}</p>}
                    </div>
                  ))}
                </details>
              ))}
            </div>
          )}
          {!!history.closings.length && (
            <details>
              <summary>Fechamentos registrados · {history.closings.length}</summary>
              {history.closings.map((closing) => (
                <div key={closing.id}>
                  <strong>{closing.publication?.title || closing.mediaKey}</strong>
                  {closing.publication && (
                    <p>
                      <a href={closing.publication.url} target="_blank" rel="noreferrer">
                        {closing.publication.account} · Ver publicação
                      </a>
                    </p>
                  )}
                  <p>
                    Publicado em {stamp(closing.publishedAt)} · coletado em{' '}
                    {stamp(closing.collectedAt)} · idade{' '}
                    {(closing.ageSeconds / 86400).toLocaleString('pt-BR', {
                      maximumFractionDigits: 2,
                    })}{' '}
                    dias.
                  </p>
                </div>
              ))}
            </details>
          )}
        </>
      )}
      {loading && !history && <p role="status">Carregando histórico…</p>}
    </div>
  );
}
